import React, { useEffect, useRef, useState } from "react";
import { FiLogOut, FiMenu, FiX } from "react-icons/fi";
import { Offcanvas } from "react-bootstrap";
import { NavLink, useLocation, useNavigate } from "react-router-dom";
import Routes from "../../routes";
import SidebarMenu from "./SidebarMenu";
import { toast } from "react-hot-toast";
import FoxberryLogo from "../../Images/large-logo.jpg";
import DarkModeLogo from "../../Images/dark_mode_light_logo.png";
import ThemeControl from "../../feature/theme/ThemeControl";
import { useTheme } from "../../feature/theme/ThemeProvider";
import { useRoutes } from "../../routes/hook";
import { useDispatch } from "react-redux";
import { setIsloggedIn } from "../../feature/Login/slice";

const SideBar = ({ children }) => {
  const { resolvedTheme } = useTheme();
  const logo = resolvedTheme === "dark" ? DarkModeLogo : FoxberryLogo;
  const { isLoggedIn } = useRoutes();
  const { routes } = Routes(isLoggedIn);

  const navigate = useNavigate();
  const { pathname } = useLocation();
  const dispatch = useDispatch()
  const [isDesktop, setIsDesktop] = useState(() => window.matchMedia("(min-width: 1024px)").matches);
  const [isOpen, setIsOpen] = useState(false);
  const mainRef = useRef(null);

  useEffect(() => {
    const media = window.matchMedia("(min-width: 1024px)");
    const handleResize = () => {
      setIsDesktop(media.matches);
      if (media.matches) setIsOpen(false);
    };
    media.addEventListener("change", handleResize);
    return () => media.removeEventListener("change", handleResize);
  }, []);

  useEffect(() => {
    setIsOpen(false);
  }, [pathname]);

  const handleLogout = () => {
    setIsOpen(false);
    dispatch(setIsloggedIn(false))
    toast("User logout successfully...  ", {
      duration: 3000,
      style: {
        backgroundColor: "var(--surface-raised)",
        fontSize: 14,
      },
    });
    navigate('/login')
  }

  return (
    <div className="main-container">
      <a className="skip-content" href="#main-content">Skip to content</a>
      <header className="mobile-navigation" {...(isOpen ? { inert: "" } : {})}>
        <button type="button" className="navigation-toggle" onClick={() => setIsOpen(true)} aria-label="Open navigation" aria-expanded={isOpen} aria-controls="primary-sidebar" title="Open navigation">
          <FiMenu aria-hidden="true" />
        </button>
        <img src={logo} alt="Foxberry" />
      </header>
      <Offcanvas
        id="primary-sidebar"
        className="sidebar"
        show={isOpen && !isDesktop}
        onHide={() => setIsOpen(false)}
        renderStaticNode
        aria-label="Navigation"
        onKeyDown={(event) => {
          if (!isOpen || isDesktop || event.key !== "Tab") return;
          const controls = Array.from(event.currentTarget.querySelectorAll('a[href], button:not([disabled])'))
            .filter((element) => !element.closest('[hidden], .dropdown-menu:not(.show)'));
          const first = controls[0];
          const last = controls[controls.length - 1];
          if (event.shiftKey && (document.activeElement === first || document.activeElement === event.currentTarget)) {
            event.preventDefault();
            last?.focus();
          } else if (!event.shiftKey && document.activeElement === last) {
            event.preventDefault();
            first?.focus();
          }
        }}
        onExited={() => {
          if (window.matchMedia("(min-width: 1024px)").matches) mainRef.current?.focus({ preventScroll: true });
        }}
      >
        <div className="top_section">
          <img className="sidebar-logo" src={logo} alt="Foxberry" />
          <button type="button" className="navigation-toggle drawer-close" onClick={() => setIsOpen(false)} aria-label="Close navigation" title="Close navigation">
            <FiX aria-hidden="true" />
          </button>
        </div>
        <nav className="routes" aria-label="Primary navigation" onClick={(event) => {
          if (event.target.closest("a")) setIsOpen(false);
        }}>
          {routes.map((route) => {
            if (route.hidden) return null;

            if (route.subRoutes) {
              return <SidebarMenu key={route.path} route={route} />;
            }

            return (
              <NavLink
                to={route.path}
                key={route.path}
                className={({ isActive }) => {
                  const isCreatePage = (route.path === "/user-list" && pathname === "/create-client")
                    || (route.path === "/operations" && (pathname === "/create-operational" || pathname.startsWith("/operational-user/")));
                  const isChildPage = (route.path === "/bulk-upload" && pathname === "/bulk-uploads")
                    || (route.path === "/list" && (pathname === "/lists" || pathname.startsWith("/list/view/")));
                  return `link${isActive || isCreatePage || isChildPage ? " active" : ""}`;
                }}
              >
                <span className="icon" aria-hidden="true">{route.icon}</span>
                <span className="link-text">{route.name}</span>
              </NavLink>
            );
          })}
        </nav>
        <div className="sidebar-footer">
          <button type="button" className="sidebar-logout" onClick={handleLogout}>
            <FiLogOut size={20} strokeWidth={2.25} aria-hidden="true" />
            <span>Log out</span>
          </button>
          <ThemeControl placement="sidebar" />
        </div>
      </Offcanvas>
      <main id="main-content" ref={mainRef} tabIndex={-1} className="sidebar-content" {...(isOpen && !isDesktop ? { inert: "" } : {})}>{children}</main>
    </div>
  );
};

export default SideBar;
